import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { MmuUserInfo, MmuUserInfoDetails, MmuUserRoles } from '../models';
import { MmuPlanDetails } from '../models/mmu-plan-details';
import { MMU_USER_INFO_ENDPOINT } from './mmu-user-info-endpoint';


@Injectable()
export class MmuUserService {
  readonly authenticated$: Observable<boolean>;
  readonly currentPlan$: Observable<MmuPlanDetails>;
  readonly user$: Observable<MmuUserInfoDetails>;
  readonly ready$ = new Subject<boolean>();

  private _authenticated = new BehaviorSubject<boolean>(false);
  private _currentPlan = new BehaviorSubject<MmuPlanDetails | null>(null);
  private _user = new BehaviorSubject<MmuUserInfoDetails>({} as MmuUserInfoDetails);

  constructor(@Inject(MMU_USER_INFO_ENDPOINT) readonly userInfoEndpointUrl: string, private _http: HttpClient) {
    this.user$ = this._user.asObservable().pipe(distinctUntilChanged());
    this.currentPlan$ = this._currentPlan.asObservable().pipe(
      filter((plan): plan is MmuPlanDetails => plan !== null),
      distinctUntilChanged()
    );
    this.authenticated$ = this._authenticated.asObservable().pipe(distinctUntilChanged());
  }

  get authenticated(): boolean {
    return this._authenticated.getValue();
  }

  get user(): MmuUserInfoDetails {
    return this._user.getValue();
  }

  get currentPlan(): MmuPlanDetails | null {
    return this._currentPlan.getValue();
  }

  get isServiceUser(): boolean {
    return this._user.getValue().type === 'service';
  }

  get isCustomerUser(): boolean {
    return this._user.getValue().type === 'customer';
  }

  /**
   * Check if the user has assigned any roles. If not, a default 'basic' role will be added that can be used in structural
   * directives that should only display something for basic users.
   *
   * @param infoDetails A UserInfoDetails object
   */
  private static extendRoleModel(infoDetails: MmuUserInfoDetails): void {
    if (!infoDetails.roles || infoDetails.roles.length === 0) {
      infoDetails.roles = [MmuUserRoles.BASIC];
    }
  }

  /**
   * Load or update the current user object from the backend and store it in the service
   */
  load(): Observable<MmuUserInfoDetails> {
    return this.loadUserAuthInfo().pipe(
      tap((userInfo) => {
        if (userInfo?.authenticated) {
          MmuUserService.extendRoleModel(userInfo.userDetails);
          this._user.next(userInfo.userDetails);
          this._currentPlan.next(userInfo.currentPlan);
          this._authenticated.next(true);
        } else {
          this._user.next({} as MmuUserInfoDetails);
          this._currentPlan.next({} as MmuPlanDetails);
          this._authenticated.next(false);
        }
        this.ready$.next(userInfo.authenticated);
        this.ready$.complete();
      }),
      map((userInfo) => userInfo?.userDetails)
    );
  }

  /**
   * Sets new user details from other components, for optimistic use cases.
   * eg: profile settings page to update the user firstName etc.
   *
   * @param userDetails
   */
  setUserDetails(userDetails: MmuUserInfoDetails): void {
    this._user.next(userDetails);
  }

  /**
   * Get the user details.
   */
  getUserDetails(): MmuUserInfoDetails {
    return this._user.getValue();
  }

  private loadUserAuthInfo(): Observable<MmuUserInfo> {
    return this.userInfoEndpointUrl
           ? (this._http.get(this.userInfoEndpointUrl) as Observable<MmuUserInfo>)
           : (of({}) as Observable<MmuUserInfo>);
  }
}
